/**
 * Constants for Bulk Upload CSV parsing and validation.
 */
import { colorBlue500, colorOrange500, colorGreen400, colorRed400, colorGray200 } from "@amzn/meridian-tokens/base/color";
import {
    SORT_PATH_FILL_ALGORITHMS,
    SORT_PATH_PICK_ALGORITHMS,
    SortingOrder,
} from "../Constants";

/**
 * Main CSV column headers — matches the user-provided upload format.
 */
export const SORT_PATH_CSV_HEADERS = {
    NODE_ID: "nodeId",
    CYCLE: "cycle",
    NAME: "name",
    BIN_TYPE: "binType",
    EXCLUDED_AISLES: "excluded_aisles",
    EXCLUDED_BINS: "excluded_bins",
    EXCLUDED_CLUSTERS: "excluded_clusters",
    INCLUDED_SORT_ZONES: "included_sort_zones",
    INCLUDED_AISLES: "included_aisles",
    INCLUDED_COLUMNS: "included_columns",
    INCLUDED_SORT_LOCATIONS: "included_sort_locations",
    BALANCING_SORT_ZONES: "balancing_sort_zones",
    OPTIMISE_CLUSTER_USAGE: "optimise_cluster_usage",
    FILL_ALGORITHM: "fillPathAlgorithm",
    PICK_ALGORITHM: "pickPathAlgorithm",
    FILL_AISLE_ORDER_TYPE: "fill_aisle_order_type",
    FILL_AISLE_CUSTOM_SEQUENCE: "fill_aisle_custom_sequence",
    FILL_AISLE_CLUSTER_SEQUENCE: "fill_aisle_cluster_sequence",
    FILL_CLUSTER_ORDER_TYPE: "fill_cluster_order_type",
    FILL_CLUSTER_CUSTOM_SEQUENCE: "fill_cluster_custom_sequence",
    FILL_CLUSTER_SEQUENCE: "fill_cluster_sequence",
    FILL_ADTA_PRIORITIZATION_ORDER: "fill_adta_prioritization_order",
    FILL_DEPRIORITIZED_TIERS: "fill_deprioritized_tiers",
    FILL_DEPRIORITIZED_TIERS_ADTA: "fill_deprioritized_tiers_adta",
    FILL_FINGER_ORDER: "fill_finger_order",
    FILL_SORT_LOCATION_ORDER: "fill_sort_location_order",
    FILL_SPLIT_PERCENTAGE: "fill_split_percentage",
    FILL_STARTING_AISLE: "fill_starting_aisle",
    PICK_AISLE_ORDER_TYPE: "pick_aisle_order_type",
    PICK_AISLE_CUSTOM_SEQUENCE: "pick_aisle_custom_sequence",
    PICK_AISLE_CLUSTER_SEQUENCE: "pick_aisle_cluster_sequence",
    PICK_CLUSTER_ORDER_TYPE: "pick_cluster_order_type",
    PICK_CLUSTER_CUSTOM_SEQUENCE: "pick_cluster_custom_sequence",
    PICK_CLUSTER_SEQUENCE: "pick_cluster_sequence",
    PICK_COLUMN_LENGTH_ZEBRA: "pick_column_length_zebra_path",
    PICK_COLUMN_SPLIT_RATIO: "pick_column_split_ratio",
    PICK_SORT_LOCATION_ORDER: "pick_sort_location_order",
    PICK_STARTING_AISLE: "pick_starting_aisle",
};

/**
 * Cluster params CSV column headers.
 */
export const SORT_PATH_CLUSTER_PARAMS_HEADERS = {
    NODE_ID: "nodeId",
    CYCLE: "cycle",
    CLUSTER_NAME: "cluster_name",
    ADTA_ENABLED: "adta_enabled",
    BLOCK_LENGTH: "block_length",
    MAX_CAPACITY: "max_capacity",
};

/**
 * Valid split percentages for COMBINATRIX algorithm.
 */
export const VALID_SPLIT_PERCENTAGES = ["0", "0.35", "0.6", "0.75", "1"];

/**
 * Valid column lengths for PARTIAL_COLUMN_ZEBRA_PATTERN.
 */
export const VALID_COLUMN_LENGTHS = ["3", "4", "5"];

/**
 * Valid ADTA prioritization orders.
 */
export const VALID_ADTA_ORDERS = ["TOP_PRIORITY", "LOW_PRIORITY", "BOTTOM_PRIORITY"];

/**
 * Max allowed length for sort path setting name.
 */
export const MAX_NAME_LENGTH = 20;

/**
 * Max allowed included sort zones for SIDELINE.
 */
export const MAX_SIDELINE_SORT_ZONES = 4;

/**
 * Max allowed rows in a single CSV upload.
 */
export const MAX_ROWS = 20;

/**
 * Delimiters used in CSV parsing.
 */
export const CSV_DELIMITER = ",";
export const KEY_SEPARATOR = "#";
export const ARRAY_SEPARATOR = ";";
export const RANGE_SEPARATOR = ":";
export const NAME_SEPARATOR = "_";
export const LIST_SEPARATOR = ", ";
export const NEWLINE_DELIMITER = /\r?\n/;

/**
 * Included location range field keys matching backend model.
 */
export const INCLUDED_AISLE_START = "startingAisle";
export const INCLUDED_AISLE_END = "endingAisle";
export const INCLUDED_LOCATION_START = "startingLocation";
export const INCLUDED_LOCATION_END = "endingLocation";

/**
 * Default column split ratio for pick path algorithms.
 */
export const DEFAULT_COLUMN_SPLIT_RATIO = "0.5";

/**
 * Value indicating ADTA is enabled for a cluster.
 */
export const ADTA_ENABLED_VALUE = "1";

/**
 * Valid algorithm and order type lists for validation.
 */
export const VALID_FILL_ALGORITHMS = Object.values(SORT_PATH_FILL_ALGORITHMS);
export const VALID_PICK_ALGORITHMS = Object.values(SORT_PATH_PICK_ALGORITHMS);
export const VALID_ORDER_TYPES = [SortingOrder.INCREASING, SortingOrder.DECREASING, SortingOrder.CUSTOM, SortingOrder.DIVERTER_COUNT];
export const VALID_AISLE_ORDER_TYPES = [SortingOrder.INCREASING, SortingOrder.DECREASING, SortingOrder.CUSTOM];
export const VALID_PICK_AISLE_ORDER_TYPES = [SortingOrder.INCREASING, SortingOrder.DECREASING];

/**
 * UI Colors for status and action indicators.
 */
export const COLORS = {
    CREATE: colorBlue500,
    UPDATE: colorOrange500,
    SUCCESS: colorGreen400,
    FAILED: colorRed400,
    PROGRESS_BAR: colorBlue500,
    PROGRESS_BG: colorGray200,
};

/**
 * Action and status labels used in the upload UI.
 */
export const ACTION_LABELS = {
    CREATE: "CREATE",
    UPDATE: "UPDATE",
};

export const STATUS_LABELS = {
    SUCCESS: "SUCCESS",
    FAILED: "FAILED",
};

/**
 * Boolean string constants for CSV parsing.
 */
export const TRUE_STRING = "true";
export const FALSE_STRING = "false";

/**
 * Default fallback error message when API response is missing or unreadable.
 */
export const DEFAULT_ERROR_MESSAGE = "Unknown error";
